// AutoFixHealth.jsx — v160.4.99y
//
// Per-app tile that surfaces the auto-fix verification loop's signal:
// "is auto-fix Claude actually shipping fixes that work?" Renders a
// success-rate percentage + counts (verified pass / fail / pending) plus
// a leaderboard of top fix-verifying testers.
//
// Visibility model: this tile calls admin endpoints, so non-admin
// users get a silent no-render when the API returns 403. This matches
// the "graceful degradation" pattern other admin-aware components use
// in this PWA.
//
// Usage: <AutoFixHealth slug={appSlug} />
//
// The tile is meant to be inline on AppDetail.jsx underneath the build
// list. It deliberately uses the same card visual treatment as existing
// AppDetail blocks for consistency.

import React, { useEffect, useState } from 'react';
import { admin } from '../lib/api.js';

export default function AutoFixHealth({ slug }) {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [activity, setActivity] = useState(null);
  const [unhealthyMacs, setUnhealthyMacs] = useState([]);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    // Fire all admin fetches in parallel. The original two endpoints
    // were strict-required (any 403 → hide tile entirely). The two
    // v99z/v100 additions (ai activity + mac health) are best-effort:
    // a failure on those just leaves their sub-sections empty without
    // hiding the tile.
    Promise.all([
      admin.autoFixSuccess(slug, 30).catch(e => ({ _err: e })),
      admin.verifyLeaderboard(slug, 5).catch(e => ({ _err: e })),
      admin.aiActivityForApp(slug, 24).catch(() => null),
      admin.macHealth(60).catch(() => null)
    ]).then(([s, l, a, m]) => {
      if (cancelled) return;
      if (s._err || l._err) {
        setHidden(true);
        return;
      }
      setStats(s);
      setLeaderboard(l.leaderboard || []);
      if (a) setActivity(a);
      if (m && Array.isArray(m.macs)) {
        setUnhealthyMacs(m.macs.filter(x => x.health === 'unhealthy' || x.health === 'degraded'));
      }
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (hidden) return null;
  if (!stats) {
    return (
      <div className="card" style={{ marginTop: 16, padding: 16, opacity: 0.6 }}>
        <div style={{ fontSize: 12, opacity: 0.7 }}>Loading auto-fix health…</div>
      </div>
    );
  }

  const decided = stats.verified_pass + stats.verified_fail;
  const rate = stats.success_rate;
  const ratePct = rate == null ? null : Math.round(rate * 100);
  // Visual cue for the headline number. Green when >70%, amber 40–70%,
  // red <40%, gray when there's not enough data.
  let rateColor = '#999';
  let rateLabel = 'Not enough data';
  if (ratePct != null) {
    rateLabel = `${ratePct}%`;
    if (ratePct >= 70)      rateColor = '#10b981';
    else if (ratePct >= 40) rateColor = '#f59e0b';
    else                    rateColor = '#ef4444';
  }

  return (
    <div className="card" style={{ marginTop: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>🔧 Auto-fix health</h3>
        <span style={{ fontSize: 12, opacity: 0.6 }}>last 30 days</span>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Success rate</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: rateColor, lineHeight: 1 }}>
            {rateLabel}
          </div>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
            {decided > 0 ? `${stats.verified_pass} of ${decided} verified fixes worked` : 'No verified fixes yet'}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <CountRow label="Verified pass" value={stats.verified_pass} color="#10b981" />
          <CountRow label="Verified fail" value={stats.verified_fail} color="#ef4444" />
          <CountRow label="Pending"        value={stats.pending}      color="#f59e0b" />
          {stats.expired > 0 && (
            <CountRow label="Expired"      value={stats.expired}      color="#6b7280" />
          )}
        </div>
      </div>

      {leaderboard && leaderboard.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Top fix-verifiers</div>
          {leaderboard.map((row, i) => (
            <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between',
                                          alignItems: 'center', fontSize: 13, padding: '4px 0' }}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ opacity: 0.5, width: 18 }}>{i + 1}.</span>
                <span>{row.display_name || row.email || '(unnamed)'}</span>
              </span>
              <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {row.verifications} run{row.verifications === 1 ? '' : 's'}
                {' · '}
                <span style={{ color: '#10b981' }}>{row.passes}✓</span>
                {row.fails > 0 && (
                  <> {' · '}<span style={{ color: '#ef4444' }}>{row.fails}✗</span></>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* v160.5.0: AI infra signal. Surfaces subscription-Claude usage
          (so admins see they're saving API spend) AND Mac health
          (so an expired claude.ai login surfaces before all AI work
          silently fails). */}
      {(activity || unhealthyMacs.length > 0) && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
          {activity && activity.total > 0 && (
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
              <span>AI calls (24h):</span>
              {' '}
              <span style={{ color: '#10b981', fontWeight: 600 }}>{activity.completed} ✓</span>
              {activity.failed > 0 && (
                <> {' · '}<span style={{ color: '#ef4444' }}>{activity.failed} ✗</span></>
              )}
              {activity.avg_response_seconds != null && (
                <> {' · '}avg {activity.avg_response_seconds.toFixed(1)}s/call</>
              )}
            </div>
          )}
          {unhealthyMacs.length > 0 && (
            <div style={{ marginTop: 6, padding: 10, borderRadius: 8,
                            background: 'rgba(239,68,68,0.10)',
                            border: '1px solid rgba(239,68,68,0.25)' }}>
              <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginBottom: 4 }}>
                ⚠️ Mac agent issue
              </div>
              {unhealthyMacs.map(m => (
                <div key={m.mac_hostname} style={{ fontSize: 12, opacity: 0.85 }}>
                  <code>{m.mac_hostname}</code> — {m.failed}/{m.completed + m.failed} jobs failed
                  {' '}({Math.round(m.failure_rate * 100)}%).
                  {' '}<span style={{ opacity: 0.7 }}>Likely claude.ai login expired.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CountRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
      <span style={{ opacity: 0.7 }}>{label}</span>
      <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
